import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { FaCheck, FaTimes, FaHourglass, FaCalendar, FaUser, FaTrash } from 'react-icons/fa';

const AdminLeaves = () => {
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [stats, setStats] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });
    const [selectedLeave, setSelectedLeave] = useState(null);

    useEffect(() => {
        fetchLeaves();
    }, [filter]);

    const fetchLeaves = async () => {
        try {
            setLoading(true);
            const params = filter !== 'all' ? { status: filter } : {};
            const response = await api.get('/leaves', { params });

            if (response.data.success) {
                const leavesData = response.data.data;

                setLeaves(leavesData);

                setStats({
                    all: leavesData.length,
                    pending: leavesData.filter(l => l.status === 'pending').length,
                    approved: leavesData.filter(l => l.status === 'approved').length,
                    rejected: leavesData.filter(l => l.status === 'rejected').length
                });
            }
        } catch (error) {
            toast.error('Failed to fetch leave requests');
        } finally {
            setLoading(false);
        }
    };

    // ✅ SAME LOGIC AS YOUR WORKING FILE
    const updateStatus = async (leaveId, status) => {
        if (!window.confirm(`Are you sure you want to ${status} this leave request?`)) {
            return;
        }

        try {
            const response = await api.put(`/leaves/${leaveId}/status`, { status });

            if (response.data.success) {
                toast.success(`Leave ${status} successfully`);
                fetchLeaves();
            }
        } catch (error) {
            toast.error('Failed to update leave status');
        }
    };

    const deleteLeave = async (leaveId) => {
        if (!window.confirm('Are you sure you want to delete this leave request?')) {
            return;
        }

        try {
            const response = await api.delete(`/leaves/${leaveId}`);

            if (response.data.success) {
                toast.success('Leave deleted successfully');
                fetchLeaves();
            }
        } catch (error) {
            toast.error('Failed to delete leave');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'text-green-600 bg-green-100';
            case 'rejected': return 'text-red-600 bg-red-100';
            case 'pending': return 'text-yellow-600 bg-yellow-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return <FaCheck className="inline mr-1" />;
            case 'rejected': return <FaTimes className="inline mr-1" />;
            case 'pending': return <FaHourglass className="inline mr-1" />;
            default: return null;
        }
    };

    const filteredLeaves =
        filter === 'all'
            ? leaves
            : leaves.filter(l => l.status === filter);

    return (
        <div className="p-6">

            {/* HEADER */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold">All Leave Requests</h1>
                <p className="text-gray-600 mt-2">Manage leave requests from employees</p>
            </div>

            {/* STATS CARDS (EXACT STYLE) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">

                <StatCard title="Total Requests" value={stats.all} icon={<FaCalendar />} color="blue" />
                <StatCard title="Pending" value={stats.pending} icon={<FaHourglass />} color="yellow" />
                <StatCard title="Approved" value={stats.approved} icon={<FaCheck />} color="green" />
                <StatCard title="Rejected" value={stats.rejected} icon={<FaTimes />} color="red" />

            </div>

            {/* FILTER TABS (EXACT SAME) */}
            <div className="bg-white rounded-lg shadow mb-6">
                <div className="flex border-b">
                    {[
                        { key: 'all', label: 'All Requests' },
                        { key: 'pending', label: 'Pending' },
                        { key: 'approved', label: 'Approved' },
                        { key: 'rejected', label: 'Rejected' }
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setFilter(tab.key)}
                            className={`px-6 py-3 font-medium ${filter === tab.key
                                    ? 'border-b-2 border-blue-600 text-blue-600'
                                    : 'text-gray-600 hover:text-blue-600'
                                }`}
                        >
                            {tab.label} ({tab.key === 'all' ? stats.all : stats[tab.key]})
                        </button>
                    ))}
                </div>
            </div>

            {/* TABLE (EXACT STYLE) */}
            <div className="bg-white rounded-lg shadow overflow-hidden">

                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <p className="mt-4 text-gray-600">Loading leave requests...</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">

                        <table className="min-w-full divide-y divide-gray-200">

                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200">

                                {filteredLeaves.map(leave => (
                                    <tr key={leave._id} className="hover:bg-gray-50">

                                        <td className="px-6 py-4 flex items-center">
                                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                <FaUser className="text-blue-600" />
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium">{leave.user?.name}</div>
                                                <div className="text-sm text-gray-500">{leave.user?.email}</div>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 capitalize">{leave.type}</td>

                                        <td className="px-6 py-4 text-sm">
                                            {new Date(leave.startDate).toLocaleDateString()} -
                                            {new Date(leave.endDate).toLocaleDateString()}
                                        </td>

                                        <td className="px-6 py-4 max-w-xs truncate">
                                            {leave.reason}
                                        </td>

                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(leave.status)}`}>
                                                {getStatusIcon(leave.status)}
                                                {leave.status}
                                            </span>
                                        </td>

                                        <td className="px-6 py-4">
                                            <div className="flex gap-2">

                                                {leave.status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => setSelectedLeave(leave)}
                                                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                                                        >
                                                            View
                                                        </button>
                                                        <button
                                                            onClick={() => updateStatus(leave._id, 'approved')}
                                                            className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                                                        >
                                                            <FaCheck />
                                                        </button>

                                                        <button
                                                            onClick={() => updateStatus(leave._id, 'rejected')}
                                                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    </>
                                                )}

                                                <button
                                                    onClick={() => deleteLeave(leave._id)}
                                                    className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                                                >
                                                    <FaTrash />
                                                </button>

                                            </div>
                                        </td>

                                    </tr>
                                ))}

                            </tbody>
                        </table>

                        {filteredLeaves.length === 0 && (
                            <div className="text-center py-12">
                                <FaCalendar className="mx-auto text-6xl text-gray-300 mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    No Leave Requests
                                </h3>
                                <p className="text-gray-500">
                                    {filter === 'all'
                                        ? 'No leave requests found'
                                        : `No ${filter} leave requests`}
                                </p>
                            </div>
                            )}

                            {selectedLeave && (
                                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">

                                    <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">

                                        {/* CLOSE BUTTON */}
                                        <button
                                            onClick={() => setSelectedLeave(null)}
                                            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                                        >
                                            ✕
                                        </button>

                                        <h2 className="text-2xl font-bold mb-4">Leave Details</h2>

                                        <div className="space-y-3">

                                            <div>
                                                <strong>Employee:</strong> {selectedLeave.user?.name}
                                            </div>

                                            <div>
                                                <strong>Email:</strong> {selectedLeave.user?.email}
                                            </div>

                                            <div>
                                                <strong>Leave Type:</strong> {selectedLeave.type}
                                            </div>

                                            <div>
                                                <strong>Start Date:</strong>{" "}
                                                {new Date(selectedLeave.startDate).toLocaleDateString()}
                                            </div>

                                            <div>
                                                <strong>End Date:</strong>{" "}
                                                {new Date(selectedLeave.endDate).toLocaleDateString()}
                                            </div>

                                            <div>
                                                <strong>Status:</strong>{" "}
                                                <span className={`px-2 py-1 rounded ${getStatusColor(selectedLeave.status)}`}>
                                                    {selectedLeave.status}
                                                </span>
                                            </div>

                                            <div>
                                                <strong>Reason:</strong>
                                                <p className="mt-1 text-gray-700">{selectedLeave.reason}</p>
                                            </div>

                                        </div>

                                        {/* ACTION BUTTONS */}
                                        {selectedLeave.status === 'pending' && (
                                            <div className="flex gap-3 mt-6">

                                                <button
                                                    onClick={() => {
                                                        updateStatus(selectedLeave._id, 'approved');
                                                        setSelectedLeave(null);
                                                    }}
                                                    className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
                                                >
                                                    Approve
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        updateStatus(selectedLeave._id, 'rejected');
                                                        setSelectedLeave(null);
                                                    }}
                                                    className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700"
                                                >
                                                    Reject
                                                </button>

                                            </div>
                                        )}

                                    </div>
                                </div>
                            )}

                    </div>

                )}
            </div>
        </div>
    );
};

const StatCard = ({ title, value, icon, color }) => {
    const colors = {
        blue: 'text-blue-500',
        green: 'text-green-500',
        yellow: 'text-yellow-500',
        red: 'text-red-500'
    };

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-500 text-sm">{title}</p>
                    <p className="text-3xl font-bold mt-2">{value}</p>
                </div>
                <div className={`text-4xl ${colors[color]}`}>
                    {icon}
                </div>
            </div>

        </div>


    );
};

export default AdminLeaves;